import {yupResolver} from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import {Button, Grid} from '@mui/material';
import React, {useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {i18n} from 'src/i18n';
import authSelectors from 'src/modules/auth/authSelectors';
import yupFormSchemas from 'src/modules/shared/yup/yupFormSchemas';
import DivisionAutocompleteFormItem from 'src/view/division/autocomplete/DivisionAutocompleteFormItem';
import AsignadorFuncionAutocompleteFormItem from 'src/view/asignadorFuncion/autocomplete/AsignadorFuncionAutocompleteFormItem';
import InputFormItem from 'src/view/shared/form/items/InputFormItem';
import InputNumberFormItem from 'src/view/shared/form/items/InputNumberFormItem';
import SwitchFormItem from 'src/view/shared/form/items/SwitchFormItem';
import TextAreaFormItem from 'src/view/shared/form/items/TextAreaFormItem';
import {
  applyDivisionIdPolicyToPayload,
  canShowDivisionIdFieldInForm,
  isTenantFixedAdminRole,
  mapRecordOrCurrentDivisionToFormDivisionId,
} from 'src/view/shared/division/divisionIdFormPolicy';
import FormWrapper, {FormButtons} from 'src/view/shared/styles/FormWrapper';
import UserAutocompleteFormItem from 'src/view/user/autocomplete/UserAutocompleteFormItem';
import * as yup from 'yup';

const schema = yup.object().shape({
  nombre: yupFormSchemas.string(i18n('entities.subTarea.fields.nombre'), {
    required: true,
  }),
  descripcion: yupFormSchemas.string(i18n('entities.subTarea.fields.descripcion'), {
    required: true,
  }),
  posicion: yupFormSchemas.integer(i18n('entities.subTarea.fields.posicion'), {
    required: true,
  }),
  responsables: yupFormSchemas.relationToMany(i18n('entities.subTarea.fields.responsables'), {}),
  asignarFunciones: yupFormSchemas.boolean(i18n('entities.subTarea.fields.asignarFunciones'), {}),
  asignadorFunciones: yupFormSchemas.relationToMany(i18n('entities.subTarea.fields.asignadorFunciones'), {}),
  incluirAsignacionEconomica: yupFormSchemas.boolean(i18n('entities.subTarea.fields.incluirAsignacionEconomica'), {}),
  asignacionEconomica: yupFormSchemas.integer(i18n('entities.subTarea.fields.asignacionEconomica'), {}),
  divisionId: yupFormSchemas.relationToOne(i18n('entities.division.label'), {required: false}),
});

function SubTareaForm(props) {
  const tenantAccessContext = useSelector(authSelectors.selectTenantAccessContext);
  const roles = useSelector(authSelectors.selectRoles);

  const [initialValues] = useState(() => {
    const record = props.record || {};

    return {
      nombre: record.nombre,
      descripcion: record.descripcion,
      posicion: record.posicion,
      responsables: record.responsables || [],
      asignarFunciones: record.asignarFunciones,
      asignadorFunciones: record.asignadorFunciones || [],
      incluirAsignacionEconomica: record.incluirAsignacionEconomica,
      asignacionEconomica: record.asignacionEconomica,
      divisionId: mapRecordOrCurrentDivisionToFormDivisionId(record),
    };
  });

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: initialValues as any,
  });

  const showDivisionIdField = canShowDivisionIdFieldInForm(tenantAccessContext, isTenantFixedAdminRole(roles));

  const onSubmit = (values) => {
    const payload = {...values};
    applyDivisionIdPolicyToPayload(payload, tenantAccessContext, {
      treatAsTransversalSubmit: isTenantFixedAdminRole(roles),
    });
    props.onSubmit(props.record?.id, payload);
  };

  const onReset = () => {
    Object.keys(initialValues).forEach((key) => {
      form.setValue(key, initialValues[key]);
    });
  };

  const {saveLoading, modal} = props;

  return (
    <FormWrapper>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Grid spacing={2} container>
            <Grid item lg={7} md={8} sm={12} xs={12}>
              <InputFormItem name="nombre" label={i18n('entities.subTarea.fields.nombre')} required={true} autoFocus />
            </Grid>
            {showDivisionIdField && !props.disabled && (
              <Grid item lg={7} md={8} sm={12} xs={12}>
                <DivisionAutocompleteFormItem
                  name="divisionId"
                  label={i18n('entities.division.label')}
                  required={false}
                  showCreate={false}
                />
              </Grid>
            )}
            <Grid item lg={7} md={8} sm={12} xs={12}>
              <TextAreaFormItem
                name="descripcion"
                label={i18n('entities.subTarea.fields.descripcion')}
                required={true}
                autoFocus
              />
            </Grid>
            <Grid item lg={7} md={8} sm={12} xs={12}>
              <InputNumberFormItem name="posicion" label={i18n('entities.subTarea.fields.posicion')} required={true} />
            </Grid>
            <Grid item lg={7} md={8} sm={12} xs={12}>
              <UserAutocompleteFormItem
                name="responsables"
                label={i18n('entities.subTarea.fields.responsables')}
                required={false}
                showCreate={!props.modal}
                mode="multiple"
              />
            </Grid>
            <Grid item lg={7} md={8} sm={12} xs={12}>
              <SwitchFormItem name="asignarFunciones" label={i18n('entities.subTarea.fields.asignarFunciones')} />
            </Grid>
            {form.watch('asignarFunciones') ? (
              <Grid item lg={7} md={8} sm={12} xs={12}>
                <AsignadorFuncionAutocompleteFormItem
                  name="asignadorFunciones"
                  label={i18n('entities.subTarea.fields.asignadorFunciones')}
                  required={false}
                  showCreate={!props.modal}
                  mode="multiple"
                />
              </Grid>
            ) : null}

            <Grid item lg={7} md={8} sm={12} xs={12}>
              <SwitchFormItem
                name="incluirAsignacionEconomica"
                label={i18n('entities.subTarea.fields.incluirAsignacionEconomica')}
              />
            </Grid>
            {form.watch('incluirAsignacionEconomica') ? (
              <Grid item lg={7} md={8} sm={12} xs={12}>
                <InputNumberFormItem
                  name="asignacionEconomica"
                  label={i18n('entities.subTarea.fields.asignacionEconomica')}
                  required={false}
                />
              </Grid>
            ) : null}
          </Grid>
          <FormButtons
            style={{
              flexDirection: modal ? 'row-reverse' : undefined,
            }}>
            <Button
              variant="contained"
              color="primary"
              disabled={saveLoading}
              type="button"
              onClick={form.handleSubmit(onSubmit)}
              startIcon={<SaveIcon />}
              size="small">
              {i18n('common.save')}
            </Button>

            <Button disabled={saveLoading} onClick={onReset} type="button" startIcon={<UndoIcon />} size="small">
              {i18n('common.reset')}
            </Button>

            {props.onCancel ? (
              <Button
                disabled={saveLoading}
                onClick={() => props.onCancel()}
                type="button"
                startIcon={<CloseIcon />}
                size="small">
                {i18n('common.cancel')}
              </Button>
            ) : null}
          </FormButtons>
        </form>
      </FormProvider>
    </FormWrapper>
  );
}

export default SubTareaForm;
